import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, CheckCircle2, Mail } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { Button, Card, Input } from "@/components/app/Primitives";
import { StackPage, useBack } from "@/components/app/Layout";
import { CenterSpinner } from "@/components/app/Spinner";

export function DeleteAccount() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signOut, user } = useAuth();
  const back = useBack(user ? "/profile" : "/login");
  const [step, setStep] = useState("identify");
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const sendCode = async () => {
    const raw = identifier.trim();
    if (!raw) return toast.show("Enter your registered email or mobile", "error");
    setBusy(true);
    try { const r = await api("/account/deletion/request", { method: "POST", auth: false, body: { identifier: raw } }); setChannel(r.channel); setStep("verify"); toast.show(r.message, "info"); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Could not send code", "error"); } finally { setBusy(false); }
  };
  const confirmDelete = () => { if (code.trim().length !== 6) return toast.show("Enter the 6-digit code", "error"); setConfirm(true); };
  const doDelete = async () => {
    setBusy(true);
    try {
      await api("/account/deletion/verify", { method: "POST", auth: false, body: { identifier: identifier.trim(), code: code.trim() } });
      setStep("done");
      try { await signOut(); } catch { /* not signed in */ }
    } catch (e) { toast.show(e instanceof Error ? e.message : "Verification failed", "error"); } finally { setBusy(false); }
  };

  return (
    <StackPage title="Assignoo Account & Data Deletion" onBack={back} testID="delete-account-page">
      <p className="text-sm leading-5 text-a-muted">This page lets you permanently delete your Assignoo account and all associated personal data. No login required — you only need access to the email or mobile you registered with.</p>
      <Card>
        <p className="mb-2 font-extrabold text-a-text">What gets deleted?</p>
        {["Your profile (name, email, mobile, password hash, photo).", "All tasks you created and all workspaces you owned.", "Your memberships elsewhere — removed from other teams and any tasks you were assigned to.", "All active sessions, refresh tokens, push devices, OTP records, and consent audit trail."].map((l) => (
          <div key={l} className="flex gap-2 py-0.5"><span className="text-a-muted">•</span><p className="text-[13px] leading-5 text-a-text">{l}</p></div>
        ))}
        <p className="mt-2 text-[13px] font-bold text-a-danger">Deletion is permanent and cannot be undone.</p>
      </Card>
      {step === "identify" ? (
        <Card className="space-y-3">
          <p className="font-extrabold text-a-text">Step 1 — Verify it&apos;s you</p>
          <p className="text-[13px] text-a-muted">Enter the email or E.164 mobile (e.g. +9198…) registered on your Assignoo account. We&apos;ll send a 6-digit code.</p>
          <Input testID="delete-identifier" placeholder="you@example.com or +91xxxxxxxxxx" value={identifier} onChangeText={setIdentifier} onSubmitEditing={sendCode} />
          <Button testID="delete-send-code" label="Send verification code" onPress={sendCode} loading={busy} variant="danger" />
        </Card>
      ) : null}
      {step === "verify" ? (
        <Card className="space-y-3">
          <p className="font-extrabold text-a-text">Step 2 — Confirm deletion</p>
          <p className="text-[13px] text-a-muted">Enter the 6-digit code sent to your {channel === "email" ? "email inbox" : "mobile number"}. It expires in 15 minutes.</p>
          <Input testID="delete-code" placeholder="123456" inputMode="numeric" maxLength={6} value={code} onChangeText={(t) => setCode(t.replace(/[^0-9]/g, ""))} />
          <button onClick={sendCode} className="text-[13px] font-bold text-a-primary">Resend code</button>
          <Button testID="delete-confirm" label="Permanently delete my account" onPress={confirmDelete} loading={busy} variant="danger" />
          <button onClick={() => setStep("identify")} className="w-full text-center text-[13px] font-semibold text-a-muted">Cancel</button>
        </Card>
      ) : null}
      {step === "done" ? (
        <Card>
          <div className="flex flex-col items-center gap-2.5 py-2 text-center">
            <CheckCircle2 size={44} className="text-a-success" />
            <p className="text-lg font-extrabold text-a-text">Account deleted</p>
            <p className="text-[13px] text-a-muted">Your Assignoo account and all associated personal data have been permanently deleted. Thank you.</p>
            <Button label="Close" variant="secondary" onPress={() => navigate("/login", { replace: true })} />
          </div>
        </Card>
      ) : null}
      <Card>
        <p className="mb-1 font-extrabold text-a-text">Prefer email?</p>
        <p className="text-[13px] leading-5 text-a-muted">Send a deletion request from your registered email address to hshubham34@gmail.com with the subject line &quot;Delete My Assignoo Account&quot;. We&apos;ll verify your identity and complete deletion within 30 days.</p>
      </Card>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-a-border bg-a-surface text-a-text">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription className="text-a-muted">This will permanently delete your Assignoo account and all associated personal data. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-a-border bg-transparent text-a-text">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-a-danger text-white" data-testid="delete-final-confirm">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </StackPage>
  );
}

export function PrivacyPolicy() {
  const { user } = useAuth();
  const back = useBack(user ? "/profile" : "/login");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const r = await api("/legal/privacy", { auth: false }); if (alive) setPayload(r); }
      catch { if (alive) setErrored(true); } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  const contact = payload?.developer?.contact_email || "hshubham34@gmail.com";

  return (
    <StackPage title="Privacy Policy" onBack={back} testID="privacy-page" contentClassName="p-4">
      {loading ? <div className="flex flex-col items-center gap-2"><CenterSpinner /><p className="text-a-muted">Loading Privacy Policy…</p></div> : (
        <>
          {errored ? (
            <Card><div className="flex items-start gap-2"><AlertCircle size={18} className="shrink-0 text-a-warning" /><p className="text-[13px] text-a-text">Couldn&apos;t load the latest Privacy Policy. Please check your internet connection and try again.</p></div></Card>
          ) : null}
          {payload ? (
            <>
              <div className="mb-5">
                <h2 className="text-[22px] font-extrabold leading-7 text-a-text">{payload.title}</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[["Effective", payload.effective_date], ["Updated", payload.last_updated], ["Version", payload.version]].map(([l, v]) => (
                    <div key={l} className="rounded-lg border border-a-border bg-a-surface px-2.5 py-1.5"><p className="text-[10px] font-bold uppercase tracking-[1px] text-a-muted">{l}</p><p className="text-xs font-bold text-a-text">{v}</p></div>
                  ))}
                </div>
              </div>
              {(payload.sections || []).map((s) => (
                <section key={s.id} className="mb-5">
                  <h3 className="mb-2 text-base font-extrabold text-a-text">{s.title}</h3>
                  {s.body.split("\n\n").map((p, i) => <p key={i} className="mb-2 whitespace-pre-wrap text-sm leading-[22px] text-a-text">{p}</p>)}
                </section>
              ))}
              <Card>
                <p className="font-extrabold text-a-text">Contact &amp; data-deletion requests</p>
                <p className="mt-1 text-[13px] text-a-muted">{payload.developer?.legal_entity}, {payload.developer?.location}</p>
                <a href={`mailto:${contact}?subject=Assignoo%20Privacy%20Request`} className="mt-2 flex items-center gap-1.5 font-bold text-a-primary hover:underline"><Mail size={16} />{contact}</a>
              </Card>
              <p className="mt-4 text-center text-[11px] text-a-muted">Public URL · this page is accessible without a login.</p>
            </>
          ) : null}
        </>
      )}
    </StackPage>
  );
}
